import { useEffect, useRef } from 'react';

/** True when the user is typing, so single-letter shortcuts must not fire. */
export function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

export type KeyMap = Record<string, (event: KeyboardEvent) => void>;

/** Normalised key name: "a", "shift+a", "escape", "enter", "?" … */
export function keyName(event: KeyboardEvent): string {
  const key = event.key.length === 1 ? event.key.toLowerCase() : event.key.toLowerCase();
  return event.shiftKey && event.key.length === 1 && /[a-z]/i.test(event.key) ? `shift+${key}` : key;
}

/** Page-level single-key shortcuts; ignored while typing or when a modifier other than Shift is held. */
export function useKeys(map: KeyMap, enabled = true): void {
  const ref = useRef(map);
  ref.current = map;
  useEffect(() => {
    if (!enabled) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const name = keyName(event);
      if (name !== 'escape' && isTyping(event.target)) return;
      const handler = ref.current[name];
      if (handler) {
        event.preventDefault();
        handler(event);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);
}

/** Moves a cursor index within [0, length). */
export function step(index: number, delta: number, length: number): number {
  if (length === 0) return 0;
  return Math.min(length - 1, Math.max(0, index + delta));
}
